import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/button";
import { PageHeader } from "../../../components/common";
import { useToast } from "../../../components/toast-container";
import { useCreateCampaign } from "../hooks";
import {
  CampaignEditorForm,
  CampaignEditorFormHandle,
  CampaignEditorFormValues,
} from "../components/CampaignEditorForm";
import { CAMPAIGN_STATUSES } from "../types/campaign-status.constants";

function CreateCampaignPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const formRef = useRef<CampaignEditorFormHandle | null>(null);
  const { loading, error, success, createCampaign, reset } = useCreateCampaign();

  const handleBack = useCallback(() => {
    navigate("/campaign");
  }, [navigate]);

  const handleSubmit = useCallback(
    async (values: CampaignEditorFormValues) => {
      const result = await createCampaign({
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        status: values.status,
        startDate: values.startDate ? `${values.startDate}T00:00:00Z` : undefined,
        endDate: values.endDate ? `${values.endDate}T00:00:00Z` : undefined,
      });

      navigate(`/campaign/${result.id}/program/edit`, {
        state: {
          campaignName: result.name,
        },
      });
    },
    [createCampaign, navigate],
  );

  useEffect(() => {
    if (success) {
      addToast({
        type: "success",
        title: "Success!",
        message: "Campaign created successfully",
      });
      reset();
    }
  }, [success, addToast, reset]);

  return (
    <div className="min-h-full bg-[linear-gradient(180deg,#F7FBF9_0%,#FFFFFF_22%)]">
      <div className="sticky top-0 z-30 border-b border-[#E5EFE9] bg-white/92 px-6 py-4 backdrop-blur">
        <div className="mx-auto max-w-7xl">
          <PageHeader
            title="Create Campaign"
            subtitle="Campaign Builder"
            sticky={false}
            actions={
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="brand-outline"
                  onClick={handleBack}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="brand-secondary"
                  onClick={() => formRef.current?.submit()}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Create Campaign"}
                </Button>
              </div>
            }
          />
        </div>
      </div>

      <CampaignEditorForm
        ref={formRef}
        loading={loading}
        error={error}
        defaultValues={{
          name: "",
          description: "",
          status: CAMPAIGN_STATUSES.Unactive,
          startDate: "",
          endDate: "",
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

export default CreateCampaignPage;
